import { AnimatedHeading } from './AnimatedHeading'
import { useCallback, useEffect, useRef, useState, type CSSProperties } from 'react'
import { Link } from 'react-router-dom'
import { processSteps, type Locale } from '../content/site'

/**
 * Scroll-driven process timeline. This is intentionally isolated from the legacy
 * four-card section. Scrolling never gets trapped: the page continues normally.
 */
const strings = {
  fa: {
    eyebrow: 'روند انجام پروژه',
    eyebrowEn: 'WORKING PROCESS',
    introStart: 'شش قدم از',
    highlight: 'ایده',
    introEnd: 'تا انتشار',
    description: 'از اولین گفت‌وگو تا طراحی، اجرا و تحویل؛ مسیر هر پروژه را با شفافیت پیش می‌بریم. روی مراحل کلیک کنید یا صفحه را اسکرول کنید.',
    steps: 'مراحل اجرای پروژه',
    next: 'مرحله بعد',
    previous: 'مرحله قبل',
    finish: 'آماده شروع پروژه‌اید؟',
    action: 'شروع همکاری',
  },
  en: {
    eyebrow: 'Our delivery process',
    eyebrowEn: 'WORKING PROCESS',
    introStart: 'Six steps from',
    highlight: 'idea',
    introEnd: 'to launch',
    description: 'From the first conversation through design, delivery and support, we keep every stage transparent. Select a step or scroll to explore.',
    steps: 'Project delivery stages',
    next: 'Next step',
    previous: 'Previous step',
    finish: 'Ready to build something?',
    action: 'Start a project',
  },
} as const

function ProcessIcon({ index, large = false }: { index: number; large?: boolean }) {
  const props = { viewBox: '0 0 32 32', fill: 'none', stroke: 'currentColor', strokeWidth: 1.8, strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const, 'aria-hidden': true as const, className: large ? 'workflow-icon workflow-icon--large' : 'workflow-icon' }
  switch (index) {
    case 0:
      return <svg {...props}><path d="M7 5h18a4 4 0 0 1 4 4v12a4 4 0 0 1-4 4H15l-8 4v-4a4 4 0 0 1-4-4V9a4 4 0 0 1 4-4Z"/><circle cx="10" cy="15" r="1.1" fill="currentColor" stroke="none"/><circle cx="16" cy="15" r="1.1" fill="currentColor" stroke="none"/><circle cx="22" cy="15" r="1.1" fill="currentColor" stroke="none"/></svg>
    case 1:
      return <svg {...props}><rect x="3.5" y="5.5" width="25" height="23" rx="4.5"/><path d="M9 3v5M23 3v5M3.5 12h25M10 18h3M18 18h3M10 23h3M18 23h3"/></svg>
    case 2:
      return <svg {...props}><path d="M8 3h12l6 6v18a2 2 0 0 1-2 2H8a3 3 0 0 1-3-3V6a3 3 0 0 1 3-3Z"/><path d="M20 3v6h6M10 16h11M10 20h11M10 24h7"/></svg>
    case 3:
      return <svg {...props}><rect x="3.5" y="4" width="25" height="24" rx="6"/><path d="m11 21 1.8-6.1L22.1 5.6a3.1 3.1 0 0 1 4.3 4.3L17 19.2 11 21Z"/><path d="m13 15 4 4"/></svg>
    case 4:
      return <svg {...props}><rect x="2.5" y="5" width="27" height="22" rx="5"/><path d="m12 12-4 4 4 4m8-8 4 4-4 4m-3-9-2 10"/></svg>
    default:
      return <svg {...props}><path d="M16 2c4.5 4.2 7 9.6 7 15l-2 5.5H11L9 17c0-5.4 2.5-10.8 7-15Z"/><circle cx="16" cy="13" r="3"/><path d="m9 18-4 3v6l6-3m12-6 4 3v6l-6-3m-8 2 3 5 3-5"/></svg>
  }
}

export function ProcessTimeline({ locale }: { locale: Locale }) {
  const t = strings[locale]
  const sectionRef = useRef<HTMLElement | null>(null)
  const [active, setActive] = useState(0)
  const [progress, setProgress] = useState(0)
  const [revealed, setRevealed] = useState(false)

  useEffect(() => {
    const section = sectionRef.current
    if (!section || typeof IntersectionObserver === 'undefined') {
      // No animation prerequisite: CSS defaults keep the content visible.
      return
    }
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setRevealed(true)
        observer.disconnect()
      }
    }, { rootMargin: '0px 0px -10% 0px', threshold: 0.08 })
    observer.observe(section)
    return () => observer.disconnect()
  }, [])

  useEffect(() => {
    const section = sectionRef.current
    if (!section) return
    const desktop = window.matchMedia('(min-width: 961px)')
    let frame = 0
    const update = () => {
      frame = 0
      if (!desktop.matches) return
      const rect = section.getBoundingClientRect()
      const distance = Math.max(1, rect.height - window.innerHeight)
      const value = Math.max(0, Math.min(1, -rect.top / distance))
      // Only derive stage while the section is actively being viewed.
      if (rect.bottom >= 0 && rect.top <= window.innerHeight) {
        setProgress(current => Math.abs(current - value) > .002 ? value : current)
        const index = Math.min(processSteps.length - 1, Math.round(value * (processSteps.length - 1)))
        setActive(current => current === index ? current : index)
      }
    }
    const schedule = () => { if (!frame) frame = window.requestAnimationFrame(update) }
    update()
    window.addEventListener('scroll', schedule, { passive: true })
    window.addEventListener('resize', schedule)
    desktop.addEventListener('change', schedule)
    return () => {
      if (frame) window.cancelAnimationFrame(frame)
      window.removeEventListener('scroll', schedule)
      window.removeEventListener('resize', schedule)
      desktop.removeEventListener('change', schedule)
    }
  }, [])

  const select = useCallback((index: number) => {
    const next = Math.max(0, Math.min(processSteps.length - 1, index))
    setActive(next)
    setProgress(next / (processSteps.length - 1))
    const section = sectionRef.current
    if (!section || !window.matchMedia('(min-width: 961px)').matches) return
    const rect = section.getBoundingClientRect()
    const start = window.scrollY + rect.top
    const distance = Math.max(1, rect.height - window.innerHeight)
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    window.scrollTo({ top: Math.max(0, start + distance * (next / (processSteps.length - 1))), behavior: reduced ? 'auto' : 'smooth' })
  }, [])

  return (
    <section
      id="process"
      ref={sectionRef}
      className={'workflow-section' + (revealed ? ' workflow-section--revealed' : '')}
      aria-labelledby="workflow-title"
      style={{ '--workflow-progress': progress } as CSSProperties}
    >
      <div className="workflow-section__ambient workflow-section__ambient--first" aria-hidden="true" />
      <div className="workflow-section__ambient workflow-section__ambient--second" aria-hidden="true" />
      <div className="workflow-section__background" aria-hidden="true" />
      <div className="workflow-section__sticky">
        <header className="workflow-section__header">
          <p className="workflow-section__eyebrow"><span className="workflow-section__dot" />{t.eyebrow}<span lang="en" className="workflow-section__en">{t.eyebrowEn}</span></p>
          <AnimatedHeading as="h2" id="workflow-title" className="workflow-section__title">
            <span className="workflow-section__title-piece">{t.introStart}</span>{' '}
            <em className="workflow-section__title-piece workflow-section__title-piece--accent">{t.highlight}</em>{' '}
            <span className="workflow-section__title-piece">{t.introEnd}</span>
          </AnimatedHeading>
          <p className="workflow-section__lead">{t.description}</p>
        </header>

        <div className="workflow-section__layout">
          <nav className="workflow-section__steps" aria-label={t.steps}>
            <div className="workflow-section__rail" aria-hidden="true">
              <span className="workflow-section__rail-progress" />
            </div>
            <ol>
              {processSteps.map((step, index) => {
                const isActive = active === index
                const isDone = active > index
                return (
                  <li key={step.number}>
                    <button
                      type="button"
                      className={'workflow-step' + (isActive ? ' workflow-step--active' : '') + (isDone ? ' workflow-step--done' : '')}
                      onClick={() => select(index)}
                      aria-current={isActive ? 'step' : undefined}
                      aria-controls="workflow-current-detail"
                      aria-label={step.number + ' — ' + step.title[locale]}
                    >
                      <span className="workflow-step__icon"><ProcessIcon index={index} /></span>
                      <span className="workflow-step__label">
                        <span className="workflow-step__index" dir="ltr" lang="en">{step.number} <span aria-hidden="true">—</span> {step.enLabel}</span>
                        <span className="workflow-step__name">{step.title[locale]}</span>
                      </span>
                      {isDone && <span className="workflow-step__check" aria-hidden="true">✓</span>}
                    </button>
                  </li>
                )
              })}
            </ol>
            <div className="workflow-section__mobile-controls">
              <button type="button" onClick={() => select(active - 1)} disabled={active === 0} aria-label={t.previous}>‹</button>
              <span dir="ltr" lang="en">{processSteps[active].number} / 06</span>
              <button type="button" onClick={() => select(active + 1)} disabled={active === processSteps.length - 1} aria-label={t.next}>›</button>
            </div>
          </nav>

          <div id="workflow-current-detail" className="workflow-section__panel" aria-label={processSteps[active].title[locale]}>
            {processSteps.map((step, index) => {
              const selected = index === active
              return (
                <article key={step.number} className={'workflow-panel' + (selected ? ' workflow-panel--active' : '')} aria-hidden={!selected}>
                  <div className="workflow-panel__top">
                    <span className="workflow-panel__glass-icon"><ProcessIcon index={index} large /></span>
                    <span className="workflow-panel__number" aria-hidden="true" dir="ltr" lang="en">{step.number}</span>
                  </div>
                  <div className="workflow-panel__copy">
                    <span className="workflow-panel__overline" dir="ltr" lang="en">{step.number} — {step.enLabel}</span>
                    <AnimatedHeading as="h3">{step.title[locale]}</AnimatedHeading>
                    <p>{step.body[locale]}</p>
                    <div className="workflow-panel__foot">
                      <span className="workflow-panel__tag">{step.note[locale]}</span>
                      <span className="workflow-panel__counter" dir="ltr" lang="en">{step.number} / 06</span>
                    </div>
                    {index === processSteps.length - 1 && (
                      <Link to="/start-project" className="workflow-panel__cta" tabIndex={selected ? 0 : -1}>
                        {t.action}<span aria-hidden="true">↗</span>
                      </Link>
                    )}
                  </div>
                </article>
              )
            })}
          </div>
        </div>
        <div className="workflow-section__bottom" aria-hidden="true"><span>PAYE TECH</span><span>{String(Math.round(progress * 100)).padStart(2, '0')}%</span></div>
      </div>
    </section>
  )
}
